import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { staffApi } from '@/lib/api';
import { fmtEdDate } from '@/lib/tz';
import { ClipboardCheck, Clock, AlertTriangle, CalendarClock, FileSignature } from 'lucide-react';

const KIND = {
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-700', icon: AlertTriangle, prefix: 'Expired' },
  dueSoon: { label: 'Due soon', cls: 'bg-amber-100 text-amber-700', icon: CalendarClock, prefix: 'Due' },
  pending: { label: 'Pending', cls: 'bg-bone text-muted-ink', icon: Clock, prefix: 'Due' },
};

function Tile({ label, value, cls, icon: Icon, testId }) {
  return (
    <div className={`rounded-lg p-3 ${cls}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</p>
        <Icon size={15} />
      </div>
      <p className="font-heading text-2xl font-700 mt-1">{value}</p>
    </div>
  );
}

// Admin dashboard card: pending vs signed/completed vs expired activities across all active staff.
export default function AssignmentOverviewCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    staffApi.get('/staff/tasks/overview').then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { counts, attention } = data;

  return (
    <div className="bg-white rounded-xl border border-border/40 p-4" data-testid="assignment-overview-card">
      <div className="flex items-center gap-2 mb-3">
        <FileSignature size={16} className="text-saffron" />
        <h2 className="font-heading font-semibold text-charcoal">Activities &amp; Sign-off Overview</h2>
        <span className="text-xs text-muted-ink ml-auto">{counts.total} total · active staff</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Pending" value={counts.pending} cls="bg-bone" icon={Clock} testId="overview-pending" />
        <Tile label="Signed / Done" value={counts.completed} cls="bg-coriander/10 text-coriander" icon={ClipboardCheck} testId="overview-completed" />
        <Tile label="Expired" value={counts.expired} cls="bg-red-50 text-red-700" icon={AlertTriangle} testId="overview-expired" />
        <Tile label="Due in 7 days" value={counts.dueSoon} cls="bg-amber-50 text-amber-700" icon={CalendarClock} testId="overview-duesoon" />
      </div>

      {attention.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-muted-ink uppercase tracking-wide mb-2">Needs attention</p>
          <div className="divide-y divide-border/30 max-h-72 overflow-y-auto" data-testid="overview-attention-list">
            {attention.map((a) => {
              const k = KIND[a.kind];
              return (
                <Link
                  key={a.taskId}
                  to={`/admin/staff/${a.staffId}`}
                  data-testid="overview-attention-row"
                  className="flex items-center gap-3 py-2 hover:bg-bone/50 -mx-1 px-1 rounded transition-colors"
                >
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${k.cls}`}>{k.label}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-charcoal truncate">{a.staffName}</p>
                    <p className="text-xs text-muted-ink truncate">{a.title}</p>
                  </div>
                  {a.date && <span className="text-xs text-muted-ink shrink-0">{k.prefix} {fmtEdDate(a.date, { month: 'short', day: 'numeric' })}</span>}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
